/**
 * STL File Cache — IndexedDB-based browser cache for STL ArrayBuffers.
 *
 * Avoids re-downloading STL files every time the user switches between
 * STL_IN, STL_OUT, or GirasolesView tabs.
 *
 * Cache key:  projectId + fileRole (e.g. "97c8650d.../stl_in")
 * Freshness:  Validated against fileId + uploadedAt from getStlStatus().
 *             If backend reports a newer file, the cache entry is evicted.
 *
 * Storage strategy:
 *   - IndexedDB "girasoles_stl_cache" database, "files" object store
 *   - Each entry stores the raw ArrayBuffer + metadata
 *   - Max cache size: 500 MB (configurable). LRU eviction when exceeded.
 */

const DB_NAME = 'girasoles_stl_cache'
const DB_VERSION = 1
const STORE_NAME = 'files'
const MAX_CACHE_BYTES = 500 * 1024 * 1024 // 500 MB

export interface STLCacheEntry {
  /** Composite key: `${projectId}/${fileRole}` */
  key: string
  projectId: string
  fileRole: 'stl_in' | 'stl_out'
  fileId: string
  filename: string
  uploadedAt: string
  sizeBytes: number
  /** Raw STL file data */
  data: ArrayBuffer
  /** When this entry was last accessed (for LRU eviction) */
  lastAccessed: number
  /** When this entry was cached */
  cachedAt: number
}

export interface STLCacheMeta {
  fileId: string
  uploadedAt: string | null | undefined
}

function cacheKey(projectId: string, fileRole: string): string {
  return `${projectId}/${fileRole}`
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' })
        store.createIndex('lastAccessed', 'lastAccessed', { unique: false })
        store.createIndex('sizeBytes', 'sizeBytes', { unique: false })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Check if a cached STL is still fresh.
 *
 * Returns the cached ArrayBuffer if fileId + uploadedAt match,
 * or null if stale / missing.
 */
export async function getCachedSTL(
  projectId: string,
  fileRole: string,
  meta: STLCacheMeta
): Promise<ArrayBuffer | null> {
  try {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const key = cacheKey(projectId, fileRole)

    return new Promise((resolve) => {
      const getReq = store.get(key)

      getReq.onsuccess = () => {
        const entry: STLCacheEntry | undefined = getReq.result
        if (!entry) {
          db.close()
          resolve(null)
          return
        }

        // Freshness check: fileId + uploadedAt must match
        const isFresh =
          entry.fileId === meta.fileId &&
          entry.uploadedAt === (meta.uploadedAt ?? '')

        if (!isFresh) {
          // Stale — delete and return null
          store.delete(key)
          db.close()
          resolve(null)
          return
        }

        // Cache hit — update lastAccessed for LRU
        entry.lastAccessed = Date.now()
        store.put(entry)
        db.close()
        resolve(entry.data)
      }

      getReq.onerror = () => {
        db.close()
        resolve(null)
      }
    })
  } catch {
    // IndexedDB unavailable (private browsing, quota, etc.) — just miss
    return null
  }
}

/**
 * Store an STL ArrayBuffer in the cache.
 *
 * Automatically evicts oldest entries (LRU) if total cache size
 * would exceed MAX_CACHE_BYTES.
 */
export async function cacheSTL(
  projectId: string,
  fileRole: string,
  meta: STLCacheMeta & { filename: string; sizeBytes: number },
  data: ArrayBuffer
): Promise<void> {
  try {
    const db = await openDB()

    // First, check total cache size and evict if needed
    await evictIfNeeded(db, data.byteLength)

    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const now = Date.now()

    const entry: STLCacheEntry = {
      key: cacheKey(projectId, fileRole),
      projectId,
      fileRole: fileRole as 'stl_in' | 'stl_out',
      fileId: meta.fileId,
      filename: meta.filename,
      uploadedAt: meta.uploadedAt ?? '',
      sizeBytes: meta.sizeBytes,
      data,
      lastAccessed: now,
      cachedAt: now,
    }

    store.put(entry)

    return new Promise((resolve) => {
      tx.oncomplete = () => {
        db.close()
        resolve()
      }
      tx.onerror = () => {
        db.close()
        resolve() // Don't fail the app if caching fails
      }
    })
  } catch {
    // Silently fail — caching is best-effort
  }
}

/**
 * Evict oldest entries (LRU) until total cache size + newBytes < MAX_CACHE_BYTES.
 */
async function evictIfNeeded(db: IDBDatabase, newBytes: number): Promise<void> {
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const index = store.index('lastAccessed')

    // Gather all entries sorted by lastAccessed (oldest first)
    const entries: { key: string; sizeBytes: number }[] = []
    let totalSize = 0

    const cursorReq = index.openCursor()
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result
      if (cursor) {
        const entry = cursor.value as STLCacheEntry
        entries.push({ key: entry.key, sizeBytes: entry.sizeBytes })
        totalSize += entry.sizeBytes
        cursor.continue()
      } else {
        // All entries loaded — check if eviction needed
        const targetSize = MAX_CACHE_BYTES - newBytes
        if (totalSize <= targetSize) {
          resolve()
          return
        }

        // Evict oldest first until under budget
        for (const entry of entries) {
          if (totalSize <= targetSize) break
          store.delete(entry.key)
          totalSize -= entry.sizeBytes
        }
        resolve()
      }
    }

    cursorReq.onerror = () => resolve()
    tx.onerror = () => resolve()
  })
}

/**
 * Get cache statistics (for debugging / CIW commands).
 */
export async function getSTLCacheStats(): Promise<{
  entryCount: number
  totalSizeMB: number
  entries: Array<{
    key: string
    filename: string
    sizeMB: number
    cachedAt: string
    lastAccessed: string
  }>
}> {
  try {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)

    return new Promise((resolve) => {
      const getAllReq = store.getAll()
      getAllReq.onsuccess = () => {
        const all = getAllReq.result as STLCacheEntry[]
        const totalSize = all.reduce((sum, e) => sum + e.sizeBytes, 0)
        db.close()
        resolve({
          entryCount: all.length,
          totalSizeMB: Math.round((totalSize / (1024 * 1024)) * 100) / 100,
          entries: all.map((e) => ({
            key: e.key,
            filename: e.filename,
            sizeMB: Math.round((e.sizeBytes / (1024 * 1024)) * 100) / 100,
            cachedAt: new Date(e.cachedAt).toISOString(),
            lastAccessed: new Date(e.lastAccessed).toISOString(),
          })),
        })
      }
      getAllReq.onerror = () => {
        db.close()
        resolve({ entryCount: 0, totalSizeMB: 0, entries: [] })
      }
    })
  } catch {
    return { entryCount: 0, totalSizeMB: 0, entries: [] }
  }
}

/**
 * Clear the entire STL cache.
 */
export async function clearSTLCache(): Promise<void> {
  try {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).clear()
    return new Promise((resolve) => {
      tx.oncomplete = () => {
        db.close()
        resolve()
      }
      tx.onerror = () => {
        db.close()
        resolve()
      }
    })
  } catch {
    // Silently fail
  }
}
